import {
    Component,
    forwardRef,
    ForwardRefExoticComponent,
    memo,
    MemoExoticComponent,
    RefAttributes,
    useCallback,
    useState,
} from 'react';
import PhoneInput, { Country, DefaultInputComponentProps, Props, State } from 'react-phone-number-input';

import { Box } from '../Box/Box';
import { Controller, PhoneInputComponent } from './components';
import {
    countrySelectProps,
    defaultCountry as defaultCountryBasedOnLocale,
    EmptyComponent,
    InternationalIcon,
    phoneLabels,
} from './PhoneField.constants';
import { PhoneSection } from './PhoneField.styles';
import { ChildrenComponents, PhoneFieldProps } from './PhoneField.types';

/**
 * Phone Fields let users enter and edit phone numbers.
 */
export const PhoneField = memo(
    forwardRef((props, ref) => {
        const {
            value,
            onChange,
            invalid,
            hint,
            initialIsCountrySelectVisible,
            placeholder,
            endIcon,
            label,
            required,
            defaultCountry,
        } = props;

        const [isCountrySelectVisible, setIsCountrySelectVisible] = useState(initialIsCountrySelectVisible);

        const handleFocus = useCallback(() => {
            setIsCountrySelectVisible(true);
        }, []);

        const handleChange = useCallback(
            (value?: string) => {
                onChange?.(value ?? '');
            },
            [onChange]
        );

        return (
            <PhoneSection $withLabel={Boolean(label)}>
                <PhoneInput
                    ref={ref}
                    smartCaret={false}
                    smartCaretDisabled
                    international
                    defaultCountry={
                        (defaultCountry as Country) ??
                        (isCountrySelectVisible ? defaultCountryBasedOnLocale : undefined)
                    }
                    value={value}
                    onChange={handleChange}
                    countrySelectProps={countrySelectProps}
                    inputComponent={PhoneInputComponent}
                    placeholder={placeholder}
                    invalid={invalid}
                    hint={hint}
                    onFocus={handleFocus}
                    labels={phoneLabels}
                    internationalIcon={InternationalIcon}
                    countrySelectComponent={isCountrySelectVisible ? undefined : EmptyComponent}
                    endIcon={endIcon}
                    startIcon={isCountrySelectVisible ? <Box width={40} /> : undefined}
                    label={label}
                    required={required}
                />
            </PhoneSection>
        );
    })
) as MemoExoticComponent<
    ForwardRefExoticComponent<
        PhoneFieldProps &
            RefAttributes<Component<Props<DefaultInputComponentProps>, State<Props<DefaultInputComponentProps>>>>
    >
> &
    ChildrenComponents;

PhoneField.Controller = Controller;

PhoneField.displayName = 'PhoneField';
